/**
 * AudioDiagnosticsOverlay
 *
 * Diagnostics for two specific bugs:
 *  1. Waveform with 1-2s lag (hypothesis: AudioContext drift / playback lookahead)
 *  2. VAD opening on the avatar's own voice (hypothesis: AEC silently disabled,
 *     or setPlaybackActive desynced from audible playback)
 *
 * Renders a chronological log of audio events. Each problem has START and END
 * entries with duration, plus a snapshot of raw numbers at START. Designed for
 * the user to screenshot or copy the log so we can correlate timestamps after
 * the fact — not for live reading.
 *
 * Toggle with Ctrl+Shift+D, or append #diag=1 / #diag=0 to the URL.
 */

import { useEffect, useRef, useState } from 'react';

type Severity = 'bad' | 'warn' | 'info';
type ProblemId = 'avatarLoop' | 'audioDelayed' | 'audioGlitched' | 'aecOff' | 'contextDead';

interface LogEntry {
  /** ms since session start (formatted later) */
  at: number;
  kind: 'START' | 'END' | 'TICK';
  problemId?: ProblemId;
  severity?: Severity;
  /** Short reason / submessage. */
  detail: string;
  /** Snapshot of raw numbers at the moment, for correlation. */
  snapshot?: string;
}

interface ActiveProblem {
  startedAt: number;
  severity: Severity;
  detail: string;
}

interface AudioDiagnosticsSnapshot {
  contextState: AudioContextState;
  contextCurrentTime: number;
  contextSampleRate: number;
  baseLatencyMs: number;
  outputLatencyMs: number;
  microphoneReady: boolean;
  recording: {
    isRecording: boolean;
    pttMode: boolean;
    pttTransmitting: boolean;
    vadGateOpen: boolean;
    vadVoicedMs: number;
    vadNoiseFloor: number;
    lastPlaybackActiveTrueAt: number;
    lastPlaybackActiveFalseAt: number;
    micEchoCancellation: boolean | null;
    micNoiseSuppression: boolean | null;
    micAutoGainControl: boolean | null;
    micSampleRate: number | null;
    contextSampleRate: number;
  } | null;
  playback: {
    isPlaying: boolean;
    streamingActive: boolean;
    scheduledNodes: number;
    bufferedChunks: number;
    bufferedSamples: number;
    scheduleLookaheadMs: number;
    useScriptProcessor: boolean;
    bufferStarvationCount: number;
    prebufferTimeMs: number;
    learnedPrebufferMs: number;
    averageInterChunkLatencyMs: number;
    jitterDetected: boolean;
    bufferLevel: number;
  } | null;
}

interface AudioDiagnosticsOverlayProps {
  getDiagnostics: () => AudioDiagnosticsSnapshot | null;
}

const STORAGE_KEY = 'audio-diag';
const POLL_INTERVAL_MS = 250;
const DRIFT_WINDOW_MS = 2000;
const MAX_LOG_ENTRIES = 500;

export default function AudioDiagnosticsOverlay({ getDiagnostics }: AudioDiagnosticsOverlayProps) {
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const haystack = window.location.search + ' ' + window.location.hash;
      const match = haystack.match(/[?&#]diag=(\w+)/);
      const param = match?.[1];
      if (param === '1' || param === 'on') {
        localStorage.setItem(STORAGE_KEY, 'on');
        return true;
      }
      if (param === '0' || param === 'off') {
        localStorage.setItem(STORAGE_KEY, 'off');
        return false;
      }
    } catch { /* ignore */ }
    return localStorage.getItem(STORAGE_KEY) === 'on';
  });
  const [collapsed, setCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);

  const sessionStartRef = useRef<number>(Date.now());
  const logRef = useRef<LogEntry[]>([]);
  const [, forceRender] = useState(0);
  const triggerRender = () => forceRender((n) => n + 1);

  const activeRef = useRef<Partial<Record<ProblemId, ActiveProblem>>>({});
  const driftAnchorRef = useRef<{ ctxTime: number; wallTime: number } | null>(null);
  const driftRatioRef = useRef<number>(1);
  const lastStarvationCountRef = useRef<number>(0);

  // Track transitions of key flags so we can correlate gap timings (e.g. how long
  // after playback ends does VAD gate open — that's the AEC tail residue).
  const lastPlayStreamRef = useRef<boolean>(false);
  const lastPlayStreamChangeAtRef = useRef<number>(0);
  const lastPlayStreamStartedAtRef = useRef<number>(0);
  const lastVadGateRef = useRef<boolean>(false);
  const lastVadGateChangeAtRef = useRef<number>(0);
  const bootLoggedRef = useRef<boolean>(false);
  // Track learnedPrebuffer / prebufferTime evolution: these growing during a session
  // is the drift hypothesis for AEC desync.
  const lastLearnedPrebufRef = useRef<number>(-1);
  const lastPrebufRef = useRef<number>(-1);
  // Near-miss tracking: bufferLevel dipped below 0.5 without triggering full
  // starvation. These don't bump bufferStarvationCount but can still produce
  // audible clicks. Use a refractory window so a single dip doesn't spam.
  const lastNearMissAtRef = useRef<number>(0);
  const NEAR_MISS_REFRACTORY_MS = 500;

  const pushEntry = (entry: LogEntry) => {
    logRef.current.push(entry);
    if (logRef.current.length > MAX_LOG_ENTRIES) {
      logRef.current.splice(0, logRef.current.length - MAX_LOG_ENTRIES);
    }
  };

  // Toggle hotkey
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setEnabled((prev) => {
          const next = !prev;
          try { localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off'); } catch { /* ignore */ }
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      const snap = getDiagnostics();
      const wallNow = performance.now();
      const elapsed = Date.now() - sessionStartRef.current;

      if (!snap) return;

      // ── Boot info (logged once when first snapshot arrives) ────────────
      if (!bootLoggedRef.current) {
        bootLoggedRef.current = true;
        const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 120);
        pushEntry({
          at: elapsed,
          kind: 'TICK',
          severity: 'info',
          detail: `BOOT  ua=${ua}  ctxSr=${snap.contextSampleRate}  pipeline=${snap.playback?.useScriptProcessor ? 'ScriptProcessor' : 'BufferSource'}`,
        });
      }

      // ── Track transitions of playStream and vadGate ────────────────────
      // These are the two flags whose desync we suspect causes the avatar-loop bug:
      // setPlaybackActive(false) fires on the JS side, but the speaker tail keeps
      // emitting sound for some ms — the mic catches it and VAD gate opens.
      if (snap.playback) {
        const cur = snap.playback.streamingActive;
        if (cur !== lastPlayStreamRef.current) {
          if (cur) {
            // Just turned on
            lastPlayStreamStartedAtRef.current = elapsed;
            pushEntry({
              at: elapsed,
              kind: 'TICK',
              severity: 'info',
              detail: 'playStream → 1 (avatar começou)',
            });
          } else {
            // Just turned off
            const dur = elapsed - lastPlayStreamStartedAtRef.current;
            pushEntry({
              at: elapsed,
              kind: 'TICK',
              severity: 'info',
              detail: `playStream → 0 (avatar parou, durou ${dur}ms)`,
            });
          }
          lastPlayStreamRef.current = cur;
          lastPlayStreamChangeAtRef.current = elapsed;
        }
      }
      if (snap.recording) {
        const cur = snap.recording.vadGateOpen;
        if (cur !== lastVadGateRef.current) {
          // When gate opens, also log how long ago the avatar stopped (or if it's still playing).
          // That gap is the smoking gun for the avatar-loop bug.
          let gapDetail = '';
          if (cur) {
            if (lastPlayStreamRef.current) {
              const intoPlayback = elapsed - lastPlayStreamStartedAtRef.current;
              gapDetail = ` (avatar AINDA a tocar, ${intoPlayback}ms in)`;
            } else if (lastPlayStreamChangeAtRef.current > 0) {
              const since = elapsed - lastPlayStreamChangeAtRef.current;
              gapDetail = ` (${since}ms após avatar parar)`;
            }
          }
          pushEntry({
            at: elapsed,
            kind: 'TICK',
            severity: cur ? 'warn' : 'info',
            detail: `vadGate → ${cur ? 1 : 0}${gapDetail}`,
          });
          lastVadGateRef.current = cur;
          lastVadGateChangeAtRef.current = elapsed;
        }
      }

      // ── Track learnedPrebuffer / prebufferTime growth ──────────────────
      // If these grow during the session, end-to-end playback delay is increasing,
      // which would explain why the AEC desyncs after several turns.
      if (snap.playback) {
        const learned = snap.playback.learnedPrebufferMs;
        if (lastLearnedPrebufRef.current === -1) {
          lastLearnedPrebufRef.current = learned;
        } else if (Math.abs(learned - lastLearnedPrebufRef.current) >= 50) {
          pushEntry({
            at: elapsed,
            kind: 'TICK',
            severity: 'info',
            detail: `learnedPrebuf: ${lastLearnedPrebufRef.current.toFixed(0)} → ${learned.toFixed(0)}ms`,
          });
          lastLearnedPrebufRef.current = learned;
        }
        const prebuf = snap.playback.prebufferTimeMs;
        if (lastPrebufRef.current === -1) {
          lastPrebufRef.current = prebuf;
        } else if (Math.abs(prebuf - lastPrebufRef.current) >= 50) {
          pushEntry({
            at: elapsed,
            kind: 'TICK',
            severity: 'info',
            detail: `prebufferTime: ${lastPrebufRef.current.toFixed(0)} → ${prebuf.toFixed(0)}ms`,
          });
          lastPrebufRef.current = prebuf;
        }
      }

      // Detect each problem; for each, decide if it's active now and whether to start/end.
      const detected: Partial<Record<ProblemId, { severity: Severity; detail: string }>> = {};

      // contextDead
      if (snap.contextState === 'closed') {
        detected.contextDead = { severity: 'bad', detail: 'AudioContext fechado' };
      }

      // aecOff
      if (snap.recording?.micEchoCancellation === false) {
        detected.aecOff = { severity: 'bad', detail: 'browser recusou echoCancellation' };
      }

      // audioDelayed
      let delayedReason = '';
      if (snap.contextState === 'running') {
        const anchor = driftAnchorRef.current;
        if (!anchor) {
          driftAnchorRef.current = { ctxTime: snap.contextCurrentTime, wallTime: wallNow };
        } else {
          const wallDelta = wallNow - anchor.wallTime;
          if (wallDelta >= DRIFT_WINDOW_MS) {
            const ctxDelta = (snap.contextCurrentTime - anchor.ctxTime) * 1000;
            driftRatioRef.current = ctxDelta / wallDelta;
            driftAnchorRef.current = { ctxTime: snap.contextCurrentTime, wallTime: wallNow };
          }
          const driftPct = Math.abs(1 - driftRatioRef.current) * 100;
          if (driftPct > 15) delayedReason = `drift ${driftPct.toFixed(1)}%`;
        }
      } else if (snap.contextState === 'suspended') {
        delayedReason = 'context suspended';
      }
      if (!delayedReason) {
        const lat = snap.outputLatencyMs > 0 ? snap.outputLatencyMs : snap.baseLatencyMs;
        if (lat > 600) delayedReason = `outputLatency ${lat.toFixed(0)}ms`;
      }
      if (!delayedReason && snap.playback?.streamingActive && snap.playback.scheduleLookaheadMs > 600) {
        delayedReason = `lookahead ${snap.playback.scheduleLookaheadMs.toFixed(0)}ms`;
      }
      if (delayedReason) {
        detected.audioDelayed = { severity: 'bad', detail: delayedReason };
      }

      // avatarLoop — gate opens during avatar audible playback
      if (snap.recording && snap.playback) {
        const playbackAudible = snap.playback.streamingActive || snap.playback.isPlaying;
        const gateOpen = snap.recording.vadGateOpen;
        const inPTT = snap.recording.pttMode;
        if (gateOpen && playbackAudible && !inPTT) {
          detected.avatarLoop = {
            severity: 'bad',
            detail: `vadVoiced=${snap.recording.vadVoicedMs}ms, lookahead=${snap.playback.scheduleLookaheadMs.toFixed(0)}ms`,
          };
        }
      }

      // avatarLoopTail — gate is open and avatar JUST stopped (within 800ms),
      // AND the VAD's playbackActive flag is still set to true (i.e. we believe
      // audio is still leaving the speaker). Only flag this when the playbackActive
      // guard is up — otherwise it's just the user starting to talk after the
      // avatar finished, which is normal and not a bug.
      if (snap.recording && snap.playback) {
        const gateOpen = snap.recording.vadGateOpen;
        const playbackOff = !snap.playback.streamingActive && !snap.playback.isPlaying;
        const inPTT = snap.recording.pttMode;
        const sincePlayStop = elapsed - lastPlayStreamChangeAtRef.current;
        // VAD playbackActive is still true if the last True transition is more
        // recent than the last False transition.
        const vadGuardActive =
          snap.recording.lastPlaybackActiveTrueAt > snap.recording.lastPlaybackActiveFalseAt;
        if (
          gateOpen &&
          playbackOff &&
          vadGuardActive &&
          !inPTT &&
          lastPlayStreamChangeAtRef.current > 0 &&
          sincePlayStop < 800 &&
          !lastPlayStreamRef.current
        ) {
          if (!detected.avatarLoop) {
            detected.avatarLoop = {
              severity: 'bad',
              detail: `tail (${sincePlayStop}ms após avatar parar), vadV=${snap.recording.vadVoicedMs}ms`,
            };
          }
        }
      }

      // audioGlitched (cumulative buffer starvation count derivative)
      if (snap.playback) {
        const cumulative = snap.playback.bufferStarvationCount;
        if (cumulative > lastStarvationCountRef.current) {
          const delta = cumulative - lastStarvationCountRef.current;
          // Where in the playback are we when this hits? Start? Middle? End?
          let when = '';
          if (lastPlayStreamRef.current && lastPlayStreamStartedAtRef.current > 0) {
            when = ` (${elapsed - lastPlayStreamStartedAtRef.current}ms into playback)`;
          } else if (!lastPlayStreamRef.current) {
            when = ' (playStream=0 quando aconteceu)';
          }
          pushEntry({
            at: elapsed,
            kind: 'TICK',
            problemId: 'audioGlitched',
            severity: 'warn',
            detail: `+${delta} buffer starvation${delta > 1 ? 's' : ''}${when}`,
            snapshot: snapshotLine(snap),
          });
          lastStarvationCountRef.current = cumulative;
        }

        // Near-miss: bufferLevel dropped into 0.2-0.5 territory while playing.
        // Below 0.2 the playback's own monitor counts a starvation; between 0.2
        // and 0.5 the user can still hear clicks but no event is recorded.
        const lvl = snap.playback.bufferLevel;
        if (
          snap.playback.streamingActive &&
          lvl > 0.2 && lvl < 0.5 &&
          elapsed - lastNearMissAtRef.current > NEAR_MISS_REFRACTORY_MS
        ) {
          let when = '';
          if (lastPlayStreamRef.current && lastPlayStreamStartedAtRef.current > 0) {
            when = ` (${elapsed - lastPlayStreamStartedAtRef.current}ms into playback)`;
          }
          pushEntry({
            at: elapsed,
            kind: 'TICK',
            severity: 'warn',
            detail: `near-starvation: bufferLevel=${(lvl * 100).toFixed(0)}%${when}`,
            snapshot: snapshotLine(snap),
          });
          lastNearMissAtRef.current = elapsed;
        }
      }

      // Reconcile detected vs active for sticky problems (start / end transitions)
      const stickyIds: ProblemId[] = ['contextDead', 'aecOff', 'audioDelayed', 'avatarLoop'];
      for (const id of stickyIds) {
        const isActive = !!activeRef.current[id];
        const found = detected[id];
        if (found && !isActive) {
          activeRef.current[id] = {
            startedAt: elapsed,
            severity: found.severity,
            detail: found.detail,
          };
          pushEntry({
            at: elapsed,
            kind: 'START',
            problemId: id,
            severity: found.severity,
            detail: found.detail,
            snapshot: snapshotLine(snap),
          });
        } else if (!found && isActive) {
          const startedAt = activeRef.current[id]!.startedAt;
          delete activeRef.current[id];
          pushEntry({
            at: elapsed,
            kind: 'END',
            problemId: id,
            detail: `durou ${(elapsed - startedAt).toFixed(0)}ms`,
            snapshot: snapshotLine(snap),
          });
        }
      }

      triggerRender();
    };

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    tick();

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [getDiagnostics]);

  if (!enabled) return null;

  const fmtTime = (ms: number): string => {
    const totalSec = ms / 1000;
    const m = Math.floor(totalSec / 60).toString().padStart(2, '0');
    const s = Math.floor(totalSec % 60).toString().padStart(2, '0');
    const cs = Math.floor((ms % 1000) / 10).toString().padStart(2, '0');
    return `${m}:${s}.${cs}`;
  };

  const idShort = (id?: ProblemId): string => {
    switch (id) {
      case 'avatarLoop':    return 'AVATAR-LOOP ';
      case 'audioDelayed':  return 'AUDIO-DELAY ';
      case 'audioGlitched': return 'AUDIO-GLITCH';
      case 'aecOff':        return 'AEC-OFF     ';
      case 'contextDead':   return 'CTX-DEAD    ';
      default:              return '            ';
    }
  };

  const kindColour = (kind: LogEntry['kind'], severity?: Severity): string => {
    if (kind === 'END') return 'text-green-400';
    if (severity === 'bad') return 'text-red-400';
    if (severity === 'warn') return 'text-yellow-400';
    if (severity === 'info') return 'text-blue-300';
    return 'text-gray-300';
  };

  const renderLine = (entry: LogEntry): string => {
    const time = fmtTime(entry.at);
    const kind = entry.kind.padEnd(5);
    const id = idShort(entry.problemId);
    const detail = entry.detail;
    const snap = entry.snapshot ? `  | ${entry.snapshot}` : '';
    return `${time}  ${kind}  ${id}  ${detail}${snap}`;
  };

  const log = logRef.current;
  const reversed = [...log].reverse();

  const handleCopy = async () => {
    const text = log.map(renderLine).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select text in the pre tag for manual copy
      const pre = document.getElementById('audio-diag-log-pre');
      if (pre) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  const handleClear = () => {
    logRef.current = [];
    activeRef.current = {};
    sessionStartRef.current = Date.now();
    triggerRender();
  };

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed bottom-2 right-2 z-[9999] bg-black/80 text-white text-xs font-mono px-3 py-2 rounded shadow-lg pointer-events-auto"
      >
        diag ({log.length})
      </button>
    );
  }

  return (
    <div className="fixed inset-2 z-[9999] bg-black/90 text-white rounded-lg shadow-2xl flex flex-col font-mono pointer-events-auto">
      <div className="flex items-center gap-2 p-2 border-b border-white/20 text-xs">
        <span className="font-bold">audio diag</span>
        <span className="text-gray-400">({log.length} events)</span>
        <div className="flex-1" />
        <button
          onClick={handleCopy}
          className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded"
        >
          {copied ? 'copied!' : 'copy'}
        </button>
        <button
          onClick={handleClear}
          className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded"
        >
          clear
        </button>
        <button
          onClick={() => setCollapsed(true)}
          className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded"
        >
          —
        </button>
      </div>
      <pre
        id="audio-diag-log-pre"
        className="flex-1 overflow-auto text-[10px] leading-tight p-2 whitespace-pre"
      >
        {reversed.length === 0
          ? '(no events yet)'
          : reversed.map((e, i) => (
              <div key={`${e.at}-${i}`} className={kindColour(e.kind, e.severity)}>
                {renderLine(e)}
              </div>
            ))}
      </pre>
    </div>
  );
}

/**
 * One-line snapshot of raw numbers at a given moment, packed densely.
 * Format: ctx=<state>,sr=<sampleRate>,outLat=<ms>,sched=<lookaheadMs>,bufChunks=<n>,
 *         vadGate=<0|1>,vadV=<voicedMs>,nf=<noiseFloor>,playStream=<0|1>,playOn=<0|1>,
 *         pb=<0|1>,ec=<0|1|?>,ns=<0|1|?>,agc=<0|1|?>
 */
function snapshotLine(snap: AudioDiagnosticsSnapshot): string {
  const r = snap.recording;
  const p = snap.playback;
  const tri = (v: boolean | null | undefined) => (v === true ? '1' : v === false ? '0' : '?');
  const parts = [
    `ctx=${snap.contextState}`,
    `sr=${snap.contextSampleRate}`,
    // micSr right next to sr so the comparison is visible without scrolling the long line.
    // If micSr != sr the mic is being bridged into a mismatched-rate context — the suspected
    // cause of choppy capture on Bluetooth (e.g. micSr=16000 while sr=48000). They should match.
    r ? `micSr=${r.micSampleRate ?? '?'}` : 'micSr=-',
    `outLat=${snap.outputLatencyMs.toFixed(0)}`,
    `baseLat=${snap.baseLatencyMs.toFixed(0)}`,
    p ? `sched=${p.scheduleLookaheadMs.toFixed(0)}` : 'sched=-',
    p ? `bufChunks=${p.bufferedChunks}` : 'bufChunks=-',
    p ? `bufSamp=${p.bufferedSamples}` : 'bufSamp=-',
    p ? `nodes=${p.scheduledNodes}` : 'nodes=-',
    p ? `playStream=${p.streamingActive ? 1 : 0}` : 'playStream=-',
    p ? `playOn=${p.isPlaying ? 1 : 0}` : 'playOn=-',
    p ? `starv=${p.bufferStarvationCount}` : 'starv=-',
    p ? `prebuf=${p.prebufferTimeMs.toFixed(0)}` : 'prebuf=-',
    p ? `bufLvl=${(p.bufferLevel * 100).toFixed(0)}%` : 'bufLvl=-',
    p ? `learnedPrebuf=${p.learnedPrebufferMs.toFixed(0)}` : 'learnedPrebuf=-',
    p ? `interChunk=${p.averageInterChunkLatencyMs.toFixed(0)}` : 'interChunk=-',
    p ? `jitter=${p.jitterDetected ? 1 : 0}` : 'jitter=-',
    r ? `vadGate=${r.vadGateOpen ? 1 : 0}` : 'vadGate=-',
    r ? `vadV=${r.vadVoicedMs}` : 'vadV=-',
    r ? `nf=${r.vadNoiseFloor.toFixed(4)}` : 'nf=-',
    r ? `ptt=${r.pttMode ? 1 : 0}` : 'ptt=-',
    r ? `ec=${tri(r.micEchoCancellation)}` : 'ec=-',
    r ? `ns=${tri(r.micNoiseSuppression)}` : 'ns=-',
    r ? `agc=${tri(r.micAutoGainControl)}` : 'agc=-',
  ];
  return parts.join(',');
}
